/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "campaigner",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Campaigner",
          "depth": 1,
          "id": "connect-to-campaigner"
        }, {
          "value": "Connect to your data source",
          "depth": 1,
          "id": "connect-to-your-data-source"
        }, {
          "value": "Create a model",
          "depth": 1,
          "id": "create-a-model"
        }, {
          "value": "Create a sync",
          "depth": 1,
          "id": "create-a-sync"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing subscribers object",
          "depth": 1,
          "id": "syncing-subscribers-object"
        }, {
          "value": "Syncing mailing lists",
          "depth": 1,
          "id": "syncing-mailing-lists"
        }, {
          "value": "Syncing suppression lists",
          "depth": 1,
          "id": "syncing-suppression-lists"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      h3: "h3",
      strong: "strong",
      ol: "ol",
      li: "li",
      a: "a",
      img: "img",
      h4: "h4",
      code: "code",
      ul: "ul",
      pre: "pre",
      span: "span",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      em: "em"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Providing relevant and timely campaigns relies on fresh and accurate data in your Campaigner account. By automatically syncing customer data from your data warehouse into Campaigner, you don't need to worry about data consistency or staleness. Instead, you can focus on building world-class customer experiences."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-campaigner",
        children: "Connect to Campaigner"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Create an API user and retrieve the API Token from Campaigner."
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Campaigner."
        }), "\n", _jsxs(_components.li, {
          children: ["Visit ", _jsx(_components.a, {
            href: "https://secure.campaigner.com/CSB/Account/AccountUsers.aspx",
            children: "Campaigner's Users Settings Page"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Create New User"
          }), ".\n", _jsx(_components.img, {
            src: "destinations/destination-campaigner-user-info.png",
            alt: "Fill out User Information and select role"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "API user"
          }), " from the ", _jsx(_components.strong, {
            children: "User Role"
          }), " dropdown select, and fill out the information required in the ", _jsx(_components.strong, {
            children: "User Profile"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Create API Key"
          }), ".\n", _jsx(_components.img, {
            src: "destinations/destination-campaigner-ip-whitelist.png",
            alt: "Fill out User Information and select role"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Add the ", _jsx(_components.a, {
            href: "/security/networking#ip-addresses",
            children: "Hightouch IP Addresses"
          }), " to the IP Allowlist and click ", _jsx(_components.strong, {
            children: "Save"
          }), ".\n", _jsx(_components.img, {
            src: "destinations/destination-campaigner-api-key.png",
            alt: "Fill out User Information and select role"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on the newly created user and head to the ", _jsx(_components.strong, {
            children: "API Keys"
          }), " tab and copy the API key."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "add-campaigner-as-a-destination-in-hightouch",
        children: "Add Campaigner as a destination in Hightouch"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Log in to Hightouch or create a free Hightouch account"
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Destinations"
          }), " in the left sidebar"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add destination"
          }), " in the top right corner"]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Campaigner"
          }), " in the destination catalog and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Paste your ", _jsx(_components.strong, {
            children: "API Key"
          }), " and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed"]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your destination a name and unique slug (for example, Campaigner Production” and ", _jsx(_components.code, {
            children: "Campaigner-production"
          }), ")"]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your Campaigner destination."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-your-data-source",
        children: "Connect to your data source"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch sits on top of your data warehouse and can read from Snowflake, Redshift, BigQuery, Databricks, Postgres, MySQL, and many other data sources. You can even fetch data from Google Sheets and Airtable. Hightouch can help activate your data wherever it resides."
      }), "\n", _jsx(_components.p, {
        children: "We've written dedicated guides for each supported data source. Take your pick:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/airtable",
            children: "Airtable"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/clickhouse",
            children: "ClickHouse"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/elasticsearch",
            children: "Elasticsearch"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/firebolt",
            children: "Firebolt"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/google-sheets",
            children: "Google Sheets"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/looker",
            children: "Looker"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/metabase",
            children: "Metabase"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/mysql",
            children: "MySQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/rockset",
            children: "Rockset"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sftp",
            children: "SFTP"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/sqlserver",
            children: "SQL Server"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Connect to ", _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If you're missing the credentials necessary to connect to your data source,\ntry inviting your teammates to Hightouch. You can have unlimited collaborators\nin your workspace."
        })
      }), "\n", _jsx(_components.h4, {
        id: "connect-to-a-sample-database-instead",
        children: "Connect to a sample database instead"
      }), "\n", _jsxs(_components.p, {
        children: ["Waiting for credentials to your data warehouse? No problem. You can connect to one of our ", _jsx(_components.a, {
          href: "/sources/sample-data",
          children: "sample datasets"
        }), " to get started right away. For the rest of this tutorial, we'll use the ", _jsx(_components.strong, {
          children: "B2B SaaS"
        }), " sample source, which contains a ", _jsx(_components.code, {
          children: "users"
        }), " table with names, emails, locations, and other user attributes. This sample source is available when you log in for the first time—no setup required."]
      }), "\n", _jsx(_components.p, {
        children: "Next, we'll write a SQL query to define which users should be synced to our Campaigner customers resource. You can follow the same pattern to sync to other support Campaigner resources,"
      }), "\n", _jsx(_components.h3, {
        id: "create-a-model",
        children: "Create a model"
      }), "\n", _jsx(_components.p, {
        children: "In Hightouch, a model represents a query that filters or transforms the records in your data source. Models are used to determine exactly which records to sync between sources and destinations."
      }), "\n", _jsx(_components.p, {
        children: "When you connect to a data warehouse, your models will probably be written in SQL. You can also import models from tools like dbt and Looker."
      }), "\n", _jsx(_components.p, {
        children: "In this example, we'll keep it simple. Suppose you want to sync all your customers to Campaigner. To accomplish this, you'll want to create a model that looks like this:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " users;\n"]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Models"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add model"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Demo Database"
          }), " as your source and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "SQL Editor"
          }), " as your modeling method."]
        }), "\n", _jsx(_components.li, {
          children: "Paste the SQL query from above into the editor on the left side of the page."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Preview"
          }), " to execute the query."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Give your model a name and unique slug (for example, \"All Users\" and ", _jsx(_components.code, {
            children: "all-users"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "ID"
          }), " as the primary key for your model."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Finish"
          }), " to create your model."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "We just created a model using a SQL query that retrieves all our current users. Next, we'll configure our sync to add these users to Campaigner."
      }), "\n", _jsx(_components.h3, {
        id: "create-a-sync",
        children: "Create a sync"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Syncs"
          }), " in the left sidebar."]
        }), "\n", _jsxs(_components.li, {
          children: ["Click on ", _jsx(_components.strong, {
            children: "Add sync"
          }), " in the top right corner."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "All Users"
          }), " as your model and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Campaigner"
          }), " as your destination and click ", _jsx(_components.strong, {
            children: "Continue"
          }), " to proceed."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "syncing-subscribers-object",
        children: "Syncing subscribers object"
      }), "\n", _jsx(_components.h4, {
        id: "object-selection",
        children: "Object selection"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing to the following Campaigner resources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Subscribers"
          }), ". Also referred to as ", _jsx(_components.strong, {
            children: "Contacts"
          }), " on Campaigner."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsxs(_components.p, {
        children: ["This integration supports ", _jsx(_components.strong, {
          children: "Upsert"
        }), " and ", _jsx(_components.strong, {
          children: "Update"
        }), "."]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Upsert"
            }), " mode, records that aren't found in Campaigner given the provided identifier will be inserted. And existing records will be updated."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["In ", _jsx(_components.strong, {
              children: "Update"
            }), " mode, only records that are found in Campaigner given the provided identifier will be updated."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Records can be matched from your source to your Campaigner workspace by ", _jsx(_components.code, {
            children: "Email"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to sync columns from your source to the supported Campaigner fields. You can map fields in Campaigner that aren't in the table below using ", _jsx(_components.a, {
          href: "#custom-field-mapping",
          children: "custom field mapping"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Mapping"
            }), _jsx(_components.th, {
              align: "center",
              children: "Type"
            }), _jsx(_components.th, {
              align: "right",
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Source ID"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Number"
            }), _jsx(_components.td, {
              align: "right",
              children: "The ID of a valid Source this subscriber should be associated with."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Publications"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Array"
            }), _jsx(_components.td, {
              align: "right",
              children: "An array of Publication IDs to be associated with the subscriber."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Lists"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Array"
            }), _jsx(_components.td, {
              align: "right",
              children: "An array of List IDs to be associated with the subscriber."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Orders"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Array"
            }), _jsxs(_components.td, {
              align: "right",
              children: ["An array of ", _jsx(_components.a, {
                href: "https://media.campaigner.com/apidocs/edapi/#OrderAdd",
                children: "order objects"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Force"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Boolean"
            }), _jsxs(_components.td, {
              align: "right",
              children: ["If the Force parameter is ", _jsx(_components.strong, {
                children: "true"
              }), ", \"Removed\" or \"Bounced\" subscriber statuses will be ignored and set back to \"Active\""]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "Force Publications"
              })
            }), _jsx(_components.td, {
              align: "center",
              children: "Boolean"
            }), _jsxs(_components.td, {
              align: "right",
              children: ["Set this property to ", _jsx(_components.strong, {
                children: "true"
              }), " if you want to force this subscriber back into a publication they might have previously requested removal from."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h4, {
        id: "custom-field-mapping",
        children: "Custom field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch allows you to set custom attributes. Any mappings from this section will be synced as custom fields. Campaigner expects custom properties to follow a ", _jsx(_components.code, {
          children: "PascalCase"
        }), " format for both ", _jsx(_components.em, {
          children: "new"
        }), " and ", _jsx(_components.em, {
          children: "existing"
        }), " custom fields. For example, if ", _jsx(_components.code, {
          children: "First Name"
        }), " exists as a field in Campaigner, enter ", _jsx(_components.code, {
          children: "FirstName"
        }), " in our mapper. Use the same format for custom fields you want Hightouch to create in Campaigner."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-records",
        children: "Delete records"
      }), "\n", _jsx(_components.p, {
        children: "When the Hightouch sync engine recognizes that a record has been removed from your source, you can either:"
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Upsert"
        }), " mode:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Do Nothing."
        }), "\n", _jsx(_components.li, {
          children: "Clear fields that are being synced in the destination."
        }), "\n", _jsx(_components.li, {
          children: "Delete record in the destination."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In ", _jsx(_components.strong, {
          children: "Update"
        }), " mode:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Do Nothing."
        }), "\n", _jsx(_components.li, {
          children: "Clear fields that are being synced in the destination."
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Clearing fields in Campaigner through Hightouch will not clear existing\nPublications and Lists from the Subscriber's record. However, It will reset\nthe Source ID, and return the boolean mappings:", _jsx(_components.code, {
            children: "Force Publications"
          }), " and\n", _jsx(_components.code, {
            children: "Force"
          }), " to their default if they exist in your mappings."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "syncing-mailing-lists",
        children: "Syncing mailing lists"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Create a mailing list and/or add subscribers with the ", _jsx(_components.strong, {
            children: "Mailing Lists"
          }), " integration. Note that contacts must already exist in your Campaigner account to be synced to a mailing list."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-1",
        children: "Record matching"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Records can be matched from your source to your Campaigner workspace by ", _jsx(_components.code, {
            children: "Email"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "create-a-new-mailing-list",
        children: "Create a new mailing list"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To create a new mailing list, select ", _jsx(_components.code, {
            children: "Create a new segment"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "You can provide a name for this mailing list, or Hightouch will use the name of the model used to create the sync."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-mailing-list",
        children: "Use an existing mailing list"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["To use an existing mailing list, select ", _jsx(_components.code, {
            children: "Use existing segment"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["You can select any of your ", _jsx(_components.strong, {
            children: "active"
          }), " Campaigner mailing lists from the drop-down select menu."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you'd like to sync to segments dynamically, click ", _jsx(_components.code, {
            children: "Use Column"
          }), " and pick a column from your model that contains the Campaigner ", _jsx(_components.code, {
            children: "Mailing List ID"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-suppression-lists",
        children: "Syncing suppression lists"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Add subscribers to suppression lists with ", _jsx(_components.strong, {
            children: "Suppression Lists"
          }), " integration."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "record-matching-2",
        children: "Record matching"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Records can be matched from your source to your Campaigner workspace by ", _jsx(_components.code, {
            children: "Email"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "use-an-existing-suppression-list",
        children: "Use an existing suppression list"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Campaigner doesn't provide the ability to create suppression lists programmatically."
        }), "\n", _jsxs(_components.li, {
          children: ["You can select any of your ", _jsx(_components.strong, {
            children: "active"
          }), " Campaigner suppression lists from the drop-down select menu."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "error-101-email-address-has-been-screened-out",
        children: "Error: 101 Email address has been screened out"
      }), "\n", _jsxs(_components.p, {
        children: ["You may see this error on records that were rejected during an otherwise successful sync run. This is frequently due to improper formatting (", _jsx(_components.code, {
          children: "@com.com"
        }), ") or other common email validation checks."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
